/**
 * @file Single source of truth for the engine-level tunables.
 *
 * Content tables live beside this file — {@link ./Weapons.js},
 * {@link ./Enemies.js}, {@link ./Powerups.js}, {@link ./Waves.js} — so that
 * balancing content and tuning the engine never touch the same file. Colours
 * all come from {@link ./Palette.js}.
 *
 * Nothing here is a global: the object is handed to {@link ../core/Game.js Game}
 * and travels to systems inside the shared context.
 */

import { toRadians } from '../utils/MathUtils.js';
import { PALETTE } from './Palette.js';
import { Layer } from './Layers.js';

// Re-exported so consumers can keep importing layers from the config surface.
export { Layer };

/** @typedef {typeof CONFIG} GameConfig */

export const CONFIG = Object.freeze({
  engine: {
    /** Largest delta a single frame may report, to survive tab-out stalls. */
    maxDelta: 1 / 20,
    /** Window, in seconds, used to average the displayed FPS. */
    fpsSampleWindow: 0.5
  },

  renderer: {
    antialias: false, // The bloom pass resolves edges; MSAA would be wasted cost.
    shadows: true,
    maxPixelRatio: 2,
    clearColor: PALETTE.background,
    fog: { color: PALETTE.fog, near: 34, far: 88 }
  },

  /** Post-processing chain (GDD §Fase 4 — iluminación avanzada). */
  post: {
    enabled: true,
    bloom: {
      strength: 0.85,
      radius: 0.55,
      threshold: 0.22,
      /** Bloom runs at a fraction of the viewport: it is the cheapest big win. */
      resolutionScale: 0.55
    },
    grade: {
      vignette: 0.42,
      chromaticAberration: 0.0016,
      scanline: 0.045,
      saturation: 1.12
    },
    /**
     * Ties aberration/glow/saturation to the player's kill-timer ratio (the
     * same 0..1 value the debug hub already calls "Pulse"). The post-FX
     * intensity is inverted from that ratio: a full timer is calm and an empty
     * timer rides the `max` end of each range. Debug-tunable from the Post FX
     * tab (`,`).
     */
    pulse: {
      enabled: true,
      /** `damp()` lambda: higher reacts to kill-timer changes faster. */
      smoothing: 2,
      aberration: { min: 0.0, max: 0.004 },
      // Applied to UnrealBloomPass.strength. The ceiling has come down twice
      // (1.1 → 0.5 → 0.3) because a bloom that blows out at low health hides
      // the arena exactly when the player most needs to read it.
      glow: { min: 0.0, max: 0.3 },
      /**
       * "No effect" here is 1, not 0 — this is a multiplier on chroma. `min`
       * is 0 on purpose: as the kill timer drains toward empty the screen rides
       * down to full greyscale, so the drained-of-colour arena itself reads as
       * the danger cue.
       */
      saturation: { min: 0.0, max: 1.5 }
    }
  },

  camera: {
    fov: 48,
    near: 0.5,
    far: 400,
    /** Tilt from the horizontal plane; GDD §4 asks for 35°–60°. */
    tilt: toRadians(56),
    distance: 30,
    minDistance: 16,
    maxDistance: 48,
    zoomStep: 2.4,
    damping: 7,
    aimLead: 0.22,
    maxAimLead: 7,
    /** Debug-tunable world offset layered on top of the normal camera rig. */
    offset: { x: 0, y: 0, z: 0 },
    /** Fullscreen lens distortion applied by the final grade pass. */
    fisheye: 0,
    shake: {
      /** Trauma decays linearly; shake scales with trauma squared. */
      decay: 1.5,
      maxOffset: 1.7,
      maxRoll: toRadians(2.4),
      frequency: 24
    }
  },

  /**
   * Soft holes opened in walls that stand between the camera and a character.
   * The rig never rotates (GDD §4), so a wall a tile or two toward the camera
   * reliably swallows whoever is behind it; this keeps those bodies readable
   * without moving the camera or shortening the walls.
   */
  wallCutout: {
    enabled: true,
    /** Hole radius in world units, measured at the character's own distance. */
    radius: 1.9,
    /** Where the dithered rim starts, as a fraction of the radius. */
    feather: 0.45,
    /** Lift off the feet, so the hole centres on the body instead of the floor. */
    centerHeight: 0.9,
    /**
     * Characters that can open a hole at once — the player, then the nearest
     * live enemies. Every slot costs a little work on every wall pixel, and
     * changing it recompiles the wall shader.
     */
    maxCharacters: 8
  },

  lighting: {
    ambient: { color: 0x101c33, intensity: 0.85 },
    hemi: { sky: 0x2a4d78, ground: 0x05070c, intensity: 0.55 },
    key: {
      color: 0xbcd8ff,
      intensity: 1.5,
      position: [18, 34, 14],
      shadowMapSize: 2048,
      shadowExtent: 46
    },
    rim: { color: PALETTE.player, intensity: 0.9, position: [-20, 12, -18] },
    /** Pooled point lights reused by muzzle flashes and explosions. */
    dynamic: { count: 4, distance: 18, decay: 2 }
  },

  /**
   * Legacy open-arena knobs retained for any residual consumers.
   * Playable geometry comes from {@link dungeon}.
   */
  arena: {
    size: 62,
    wallThickness: 2,
    wallHeight: 2.6,
    obstacleCount: 0,
    obstacleMinSize: 2,
    obstacleMaxSize: 6,
    safeRadius: 8,
    edgeGlowHeight: 0.14,
    colors: {
      floor: PALETTE.floor,
      grid: PALETTE.grid,
      wall: PALETTE.wall,
      wallEdge: PALETTE.wallEdge,
      obstacle: PALETTE.obstacle,
      obstacleEdge: PALETTE.obstacleEdge
    }
  },

  /**
   * Procedural dungeon (threejs-procedural-dungeon pipeline).
   *
   * Only the top-level CONFIG object is frozen, so `dungeon.population` and
   * `dungeon.progression` stay runtime-mutable — the dungeon debug panel edits
   * them live and the next floor picks the change up.
   */
  dungeon: {
    // Base editable knobs — floor 1 = the original hand-tuned behaviour.
    tileSize: 2,
    roomCount: 18,
    loopChance: 0.18,
    decorDensity: 0.85,
    /** 'auto' or derelict|reactor|cryo|quarantine|hydroponics|hangar|medbay|lab */
    theme: 'auto',
    wallHeight: 2.6,
    maxGenAttempts: 5,
    maxTorchLights: 22,

    /**
     * Corridor widths, in tiles. The generator's own widths (1 for a treasure
     * spur, 2 for a normal link, 3 for the critical path) were authored for a
     * top-down map, not for fighting in: one tile is `tileSize` = 2 world units
     * against a player 1.24 wide, so a single enemy plugs the passage and there
     * is no room to strafe.
     *
     * Anything at or below `narrowWidth` is treated as narrow and multiplied by
     * `widenFactor`, then capped — spine rooms are only 3-4 tiles across, so an
     * uncapped corridor would end up wider than the rooms it connects.
     */
    corridors: {
      narrowWidth: 3,
      widenFactor: 2,
      maxWidth: 4
    },

    /**
     * Enemy pre-population (issue #44). The generator places enemies at build
     * time, deterministically from the level seed. Content tables (archetype
     * weights, formations) live in {@link ./DungeonPopulation.js}.
     */
    population: {
      /** Master switch: false leaves the dungeon empty of pre-placed enemies. */
      enabled: true,
      /**
       * Coexistence with the wave director:
       * - 'prepopulated': only pre-placed enemies; no waves.
       * - 'waves': legacy behaviour; no pre-placement, WaveSystem fills the level.
       * - 'both': pre-placed enemies plus waves (pre-placed count against
       *   maxAlive but not against the wave roster).
       */
      mode: 'prepopulated',
      /** Level-wide enemy total is a seeded roll in [minEnemies, maxEnemies]. */
      minEnemies: 14,
      maxEnemies: 46,
      /** Per-room-type count ranges (further shaped by room difficulty). */
      perRoom: {
        combat: { min: 2, max: 6 },
        elite: { min: 3, max: 5 },
        boss: { min: 2, max: 3 },
        treasure: { min: 0, max: 1 },
        shrine: { min: 0, max: 1 }
      },
      /** Hard ceiling on pre-placed enemies, coherent with WAVES.maxAlive. */
      maxPrePlaced: 90,
      /** World-unit radius around the entrance kept clear of enemies. */
      safeRadiusFromEntrance: 9,
      /** Per-floor enemy multiplier (applied when progression.enabled). */
      scaleWithLevel: { perLevel: 0.12, max: 2.5 }
    },

    /**
     * Per-floor difficulty curve (issue #44). A declarative modifier layered on
     * the base knobs above — with `enabled: false`, or on floor 1, the dungeon
     * is byte-for-byte the original. Each entry has a per-level delta and a hard
     * cap so a long run never explodes generation cost.
     */
    progression: {
      enabled: true,
      roomCount: { perLevel: 2, max: 34 },
      loopChance: { perLevel: 0.02, max: 0.4 },
      decorDensity: { perLevel: 0.03, max: 1.2 },
      /** Multiplier on population min/max; shares scaleWithLevel's cap. */
      enemies: { perLevel: 0.12, max: 2.5 }
    }
  },

  player: {
    radius: 0.62,
    height: 1.5,
    /**
     * The player has no hit points: this countdown is the whole life system.
     * It drains in real time, every kill refills it, and reaching zero is death.
     */
    killTimerMax: 10,
    /** Seconds burned off the countdown per point of damage that beats the shield. */
    killTimerDamagePenalty: 0.1,
    speed: 10,
    acceleration: 85,
    deceleration: 70,
    invulnerabilityTime: 0.45,
    footstepInterval: 0.32,
    /** Ground-plane height the cursor is projected onto when aiming. */
    aimHeight: 0.85,
    color: PALETTE.player,
    barrelColor: PALETTE.playerCore,
    trail: {
      /** Emits one trail puff per this many units travelled. */
      distance: 0.55,
      color: PALETTE.playerTrail,
      size: 0.26,
      life: 0.34
    },
    /**
     * What taking a real hit looks like (issue #74). Both effects fire from the
     * one branch in `HealthSystem` that has already cleared shields, i-frames
     * and god mode, so a swallowed hit is silent.
     */
    damageFeedback: {
      /** Camera trauma added on a hit; moved here out of `feel.trauma`. */
      shake: 0.32,
      /** Flash tint, packed `0xRRGGBB`. Code-only: colours are not sandbox knobs. */
      flashColor: 0xff365e,
      /** Seconds the screen flash stays up before it fades. */
      flashDuration: 0.22
    },
    /**
     * Mouse2 / LB dash. Stun+disarm on the first enemy hit; no damage.
     * Tunable live via the Player sandbox (nested `dash.*` paths).
     */
    dash: {
      distance: 6,
      duration: 0.12,
      cooldown: 0.9,
      radius: 0.7,
      invulnerability: 0.12,
      shake: 0.08
    }
  },

  /**
   * Player ammo economy. Every weapon instance rolls a random reserve pool
   * (0..magazineCapacity*2, see `Weapon#constructor`) the moment it is built.
   * `emptyMagazinePolicy` is the single switch for what an empty mag means:
   *  - `free_reload`   — R / dry trigger draws from that reserve pool (default)
   *  - `no_reload`     — cannot reload; gun is spent until swapped
   *  - `unequip_empty` — empty gun is dropped; fallback knife is issued
   */
  ammo: {
    emptyMagazinePolicy: 'free_reload'
  },

  /** Contextual `E` interaction: dropped weapons, and anything added later. */
  interaction: {
    /** Distance at which an interactable offers its prompt. */
    radius: 2,
    /** Height above the interactable that the prompt floats at. */
    promptHeight: 0.95
  },

  physics: {
    cellSize: 4,
    skin: 0.001,
    dynamicResolveBias: 0.5
  },

  /**
   * Bullets shove what they hit (issue #75). The impulse lands on
   * `Velocity.knockback`, which the movement system integrates outside the
   * steering clamp — a push has to beat the target's own top speed to read as
   * one — and bleeds off at {@link decay}.
   */
  knockback: {
    /** Impulse a bullet adds, units/second, before the weapon's multiplier. */
    bulletImpulse: 7,
    /** Impulse an enemy melee hit adds to the player, away from the attacker. */
    contactImpulse: 11,
    /**
     * Ceiling on the impulse a body may be carrying. It is a ceiling on the
     * vector itself, not a per-hit budget, so a shotgun blast landing five
     * pellets on one enemy pushes it no harder than a single pellet would.
     */
    maxImpulse: 11,
    /** How fast a push bleeds off, units/second². */
    decay: 42
  },

  particles: {
    max: 1400,
    muzzleCount: 6,
    impactCount: 10,
    deathCount: 26,
    explosionCount: 46,
    telegraphCount: 18,
    /** Particles laid along a melee swing arc (issue #122). */
    meleeArcCount: 22,
    gravity: -14,
    drag: 2.4
  },

  /**
   * Throwing the equipped weapon, and what a weapon on the floor does
   * afterwards (issue #15).
   */
  throwing: {
    /** Launch speed, units/second. Fast enough to read as a throw, slow enough not to tunnel. */
    speed: 30,
    /** Distance the weapon flies before dropping to the floor. */
    range: 16,
    /** Height it flies at; also the collider half-extent source for the sweep. */
    height: 0.85,
    /** Collider radius while airborne. */
    radius: 0.3,
    /** Seconds a weapon lying on the floor survives before despawning. */
    lifetime: 30,
    /** Camera trauma added on release. */
    shake: 0.12,
    /** Seconds the first enemy hit stays stunned. */
    stunDuration: 2,
    /** Stars orbiting a stunned enemy, and how often the ring is re-emitted. */
    stunStarCount: 4,
    stunStarInterval: 0.3,
    stunStarRadius: 0.8,
    /** Radians the star ring advances between emissions, so it reads as spinning. */
    stunStarSpin: 1.1,
    /** How close an enemy must get to a floor weapon to equip it. */
    enemyPickupRadius: 1
  },

  /** Frame-level feedback (GDD §Fase 4 — pulido). */
  feel: {
    /** Seconds of slow motion when something dies, and how slow. */
    hitStopDuration: 0.07,
    hitStopScale: 0.16,
    /**
     * Camera trauma added by each event, 0..1. Player hits are not here: their
     * shake is player-owned and live-tunable, in `player.damageFeedback.shake`.
     */
    trauma: { death: 0.16, explosion: 0.6, wave: 0.22 }
  },

  audio: {
    masterVolume: 0.32,
    minInterval: 0.02
  },

  /**
   * Dynamic layered music (see {@link ../audio/MusicDirector.js} and
   * {@link ../systems/MusicCueSystem.js}). Five 3-second stems — SNARE always
   * on, SCRATCH/SYNTH/KICK/BASS2 mixed in or out — loop in permanent phase
   * lock; layer changes only ever land on a bar boundary.
   */
  music: {
    volume: 0.5,
    /**
     * Expected length of every layer, in seconds; also the bar-quantization
     * grid. The engine measures the real decoded length of SNARE and uses
     * that instead, so a stem a few milliseconds off nominal still loops
     * without drift — this value is a fallback and a sanity-check reference.
     */
    barDuration: 3,
    /** Minimum speed, units/second, counted as "moving" for SCRATCH. */
    moveSpeedThreshold: 0.3,
    /** SYNTH: radius (world units) and cone half-width of "an enemy is in view". */
    sightRadius: 22,
    sightFovDegrees: 100,
    /** BASS2 stays in after the player's last kill, in seconds. */
    killGraceDuration: 8,
    /**
     * TIME RUNNING OUT: pitch shift once the kill timer drops to
     * `hud.lowKillTimerThreshold`. Like every other layer change, this only
     * lands on a bar boundary — never mid-bar. "+1 tone" is a whole step
     * (2 semitones), applied via `detune` so it is a pure repitch — Web Audio
     * has no way to shift pitch without also changing playback speed on a
     * plain buffer source, so the loop plays back faster while this is
     * active. Bar length is recomputed from the boundary where the shift
     * lands onward; see `MusicDirector._commitBoundary`.
     */
    pulseSemitones: 2,
    /**
     * Once a layer turns on, it must play this many full loops before it is
     * allowed to turn off again — a stray half-second stop doesn't chop the
     * layer right back out. A fresh activation while still inside this
     * window (the player started moving again before the window elapsed)
     * restarts the count from that point; see `MusicDirector._commitBoundary`.
     * Does not apply to the pitch shift, which is not a track.
     */
    minActiveLoops: 3,
    /** Seconds of linear ramp a layer's gain uses at a bar boundary, to step on/off without an audible click. */
    declickTime: 0.015,
    /** How far ahead of a boundary (seconds) the per-frame scheduler locks in its gain automation. Must comfortably exceed a dropped frame. */
    schedulerLookahead: 0.25
  },

  hud: {
    /**
     * Multiplier applied to every HUD element (text, bars, crosshair, overlays).
     * 1 is the size the CSS is authored at; the default triples it so the HUD
     * stays readable on the arcade/stream-sized screens the game is played on.
     * Published to CSS as `--ui-scale` and honoured by the JS-positioned bits.
     */
    scale: 3,
    /** Per-surface multipliers layered on top of `scale` by the debug UI tab. */
    componentScales: {
      crosshair: 0.8,
      timer: 0.85,
      objective: 0.85,
      weapon: 0.9,
      status: 1,
      notifications: 1,
      prompts: 1,
      banner: 1
    },
    /** Title shown above the top-center kill countdown. */
    killTimerLabel: 'Kill before time runs out',
    /** Countdown ratio at or below which the kill timer turns red and pulses. */
    lowKillTimerThreshold: 0.5,
    /** Shield points that fill the shield bar; the bar has no health pool to track now. */
    shieldScale: 60,
    /** Seconds a wave banner stays on screen. */
    bannerDuration: 2.4,
    /** Corner toast stack that gives micro-feedback for gameplay events. */
    notifications: {
      /** Most toasts shown at once; older ones are discarded FIFO. */
      maxStack: 5,
      /** Fallback lifetime when a type has no explicit duration. */
      baseDuration: 2.4,
      /** Seconds each toast type lingers before its exit animation. */
      durations: {
        weapon: 2.0,
        pickup: 2.4,
        kill: 3.0,
        warning: 4.0,
        damage: 2.4,
        status: 2.4,
        default: 2.4
      }
    }
  },

  debug: {
    showFps: true,
    drawColliders: false,
    /** Shows the player weapon's internal state (ammo, cadence, last shot). */
    showWeapon: false,
    /** Logs blast radius, cause and self-damage for every explosion. */
    logExplosions: false,
    /**
     * Wave sandbox (`ñ`). Always available on a dev server; set this to true to
     * keep it in a production build for a balancing session.
     */
    waveInspector: false,
    /**
     * Dungeon population sandbox (`-`). Same rule as {@link waveInspector}:
     * always on a dev server, opt-in for a shipped build (issue #44).
     */
    dungeonInspector: false
  }
});
